import { motion } from 'framer-motion';
import { Award, Briefcase, Camera, Code, Film, Globe, GraduationCap, Mail, Phone, Sparkles, Target, Users } from 'lucide-react';
import { IMAGES } from '@/assets/images';
import Layout from '@/components/Layout';
import { EXECUTIVE_ROLES } from '@/lib/index';

export default function About() {
  const creativePillars = [
    {
      icon: Users,
      title: 'Character-Centric Commercialism',
      description: 'Crafting commercially viable narratives anchored in authentic, multi-dimensional characters that resonate with global audiences.'
    },
    {
      icon: Camera,
      title: 'Visual & Aesthetic Identity',
      description: 'Establishing distinctive visual signatures through meticulous cinematography, color grading, and production design that elevate storytelling.'
    },
    {
      icon: Film,
      title: 'Architectural Narrative Layering',
      description: 'Building complex, interconnected story structures that reward multiple viewings and create immersive cinematic universes.'
    },
    {
      icon: Target,
      title: 'Strategic Genre Synthesis',
      description: 'Blending genre conventions to create innovative hybrid narratives that transcend traditional categorization and expand market appeal.'
    }
  ];

  const competencies = [
    { icon: Briefcase, label: 'Corporate Governance' },
    { icon: Film, label: 'Content Strategy' },
    { icon: Code, label: 'Technological Innovation' },
    { icon: Globe, label: 'Global Distribution' },
    { icon: Sparkles, label: 'Business Development' },
    { icon: Award, label: 'Digital Monetization' }
  ];

  const technologies = [
    'ARRI Alexa',
    'RED Series',
    'Panavision',
    'Phantom',
    'Ronin 4D',
    '35mm Film',
    'Final Cut Pro X',
    'Adobe Premiere Pro',
    'DaVinci Resolve'
  ];

  const education = [
    {
      degree: 'MFA Film Direction & Cinematography',
      institution: 'Toronto Film School',
      year: '2008'
    },
    {
      degree: 'Specialized Certificate 3D Camera',
      institution: 'CFI San Diego',
      year: '2010'
    },
    {
      degree: 'Advanced Diploma Multimedia',
      institution: 'Arena',
      year: '2002'
    },
    {
      degree: 'Bachelor of Arts',
      institution: 'MLSU Udaipur',
      year: '2000'
    }
  ];

  const affiliations = [
    'LIFT (Liaison of Independent Filmmakers of Toronto)',
    'IFTDA (Indian Film & Television Directors Association)',
    'WIFPA (Western India Film Producers Association)',
    'FWA (Film Writers Association)',
    'IMPPA (Indian Motion Picture Producers Association)',
    'Association of Film & Video Editors'
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <section className="relative min-h-[70vh] md:min-h-screen flex items-end justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.KESHHAV_DIRECTOR_12_ZOOMIN_1_12}
              alt="Keshhav Panneriy"
              className="w-full h-full object-cover object-top opacity-56"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/20 via-transparent to-background/90" />
          </div>

          <div className="relative z-10 container mx-auto px-4 sm:px-6 pb-12 md:pb-20 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="font-display text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-foreground mb-6 md:mb-8">
                Cinema is not just created —<br />
                <span className="text-accent">it is engineered.</span>
              </h1>
              <div className="w-24 md:w-32 h-1 bg-accent mx-auto" />
            </motion.div>
          </div>
        </section>

        <section className="py-12 md:py-24 bg-card">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <img
                  src={IMAGES.KESHHAV_DIRECTOR_8_1_7}
                  alt="Keshhav Panneriy"
                  className="w-full rounded-lg shadow-cinematic"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 md:mb-6">
                  Executive Summary
                </h2>
                <div className="space-y-4 text-muted-foreground text-sm sm:text-base md:text-lg leading-relaxed text-justify">
                  <p>
                    Keshhav Panneriy is a strategic systems architect who conceptualizes cinema as a sophisticated ecosystem of narrative, performance, and visual design. His professional foundation is predicated upon over two decades of immersive engagement in the performance arts—cultivating a granular understanding of psychological depth and <em>mise-en-scène</em>—and more than fifteen years of professional filmmaking, augmented by academic credentials from the <span className="text-foreground font-semibold">Toronto Film School</span>.
                  </p>
                  <p>
                    A distinguished leader synthesizing the intersection of creative narrative and strategic business operations, he utilizes a hybrid methodology that integrates rigorous American pre-production standards with Indian mass-market sensibilities to facilitate cinematic content viable for global theatrical and OTT dissemination.
                  </p>
                  <p>
                    Recognized as a vanguard in the integration of emerging technologies, including <span className="text-accent font-semibold">AI-augmented filmmaking</span>, CGI-VFX hybrid workflows, and stereoscopic 3D production pipelines. Possesses comprehensive executive acumen in P&L oversight, intellectual property development, investor relations, and digital monetization strategies across global OTT and broadcast infrastructures.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-10 md:mb-16"
            >
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4">
                Four Creative Pillars
              </h2>
              <p className="text-muted-foreground text-sm md:text-lg max-w-3xl mx-auto">
                The foundational principles that define Keshhav's cinematic philosophy and approach to storytelling.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
              {creativePillars.map((pillar, index) => {
                const Icon = pillar.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-card p-5 md:p-8 rounded-lg shadow-cinematic border border-border hover:border-accent transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                        <Icon className="w-6 h-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-display text-xl font-bold text-foreground mb-3">
                          {pillar.title}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          {pillar.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-24 bg-card">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4">
                Executive Experience
              </h2>
              <p className="text-muted-foreground text-sm md:text-lg max-w-3xl mx-auto">
                A proven track record of leadership across production, technology, and education sectors.
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto space-y-8">
              {EXECUTIVE_ROLES.map((role, index) => (
                <motion.div
                  key={role.id}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-background p-5 md:p-8 rounded-lg shadow-cinematic border-l-4 border-accent"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                    <div>
                      <h3 className="font-display text-lg md:text-2xl font-bold text-foreground mb-1">
                        {role.title}
                      </h3>
                      <p className="text-accent font-semibold">{role.company}</p>
                    </div>
                    <p className="text-muted-foreground mt-2 md:mt-0">{role.period}</p>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    {role.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-10 md:mb-16"
            >
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4">
                Core Competencies
              </h2>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-5xl mx-auto">
              {competencies.map((comp, index) => {
                const Icon = comp.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="bg-card p-6 rounded-lg shadow-cinematic text-center hover:shadow-gold transition-shadow"
                  >
                    <Icon className="w-8 h-8 text-accent mx-auto mb-3" />
                    <p className="text-sm font-semibold text-foreground">{comp.label}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-24 bg-card">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-5 md:mb-8">
                  Technology & Systems
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {technologies.map((tech, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      className="bg-background p-4 rounded-lg border border-border hover:border-accent transition-colors"
                    >
                      <p className="text-foreground font-semibold text-center">{tech}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-5 md:mb-8">
                  Education
                </h2>
                <div className="space-y-6">
                  {education.map((edu, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="bg-background p-6 rounded-lg border-l-4 border-accent"
                    >
                      <div className="flex items-start gap-3">
                        <GraduationCap className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="font-bold text-foreground mb-1">{edu.degree}</h3>
                          <p className="text-muted-foreground">{edu.institution}</p>
                          <p className="text-accent text-sm mt-1">{edu.year}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-8 md:mb-12"
            >
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4">
                Industry Affiliations
              </h2>
            </motion.div>

            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-4">
              {affiliations.map((affiliation, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="bg-card p-4 rounded-lg border border-border text-center"
                >
                  <p className="text-foreground font-medium">{affiliation}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-32 bg-gradient-to-b from-card to-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-5 md:mb-8">
                Let's Create Together
              </h2>
              <p className="text-muted-foreground text-sm md:text-lg lg:text-xl mb-8 md:mb-12">
                Ready to bring your cinematic vision to life? Connect with Keshhav Panneriy to discuss your next project.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-8 max-w-2xl mx-auto">
                <motion.a
                  href="mailto:info@keshhavpanneriy.com"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-accent text-accent-foreground p-6 md:p-8 rounded-lg shadow-gold hover:shadow-cinematic transition-all"
                >
                  <Mail className="w-8 h-8 mx-auto mb-4" />
                  <p className="font-semibold text-lg mb-2">Email</p>
                  <p className="text-sm">info@keshhavpanneriy.com</p>
                </motion.a>

                <motion.a
                  href="tel:+919833447935"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-accent text-accent-foreground p-6 md:p-8 rounded-lg shadow-gold hover:shadow-cinematic transition-all"
                >
                  <Phone className="w-8 h-8 mx-auto mb-4" />
                  <p className="font-semibold text-lg mb-2">Phone</p>
                  <p className="text-sm">+91 98334 47935</p>
                </motion.a>
              </div>

              <p className="text-muted-foreground mt-8 text-lg">
                Mumbai, India · Hollywood, USA · Global
              </p>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}